import querystring, { type ParsedUrlQuery, type ParsedUrlQueryInput } from 'querystring';
import { filterXSS } from 'xss';
import type { Filters, OverrideValues } from '@/interfaces';
import {
    checkboxMapToQuery,
    destinationsToQuery,
    flexibleDateToQuery,
    monthsToQuery,
    numberRangeToQuery,
    numbersToQuery,
    numberToQuery,
    queryToCheckboxMap,
    queryToDestinations,
    queryToFlexibleDate,
    queryToMonths,
    queryToNumber,
    queryToNumberRange,
    queryToNumbers,
    queryToString,
    stringToQuery,
} from './queryParamConverters';
import { fromUrlValues, toUrlValues } from './urlEnumConversion';
import { browserUrlDisplayValues } from './urlEnumMappings';
import { fromUrlKeys, toUrlKey, toUrlKeys } from './urlKeyConversion';
import { apiUrlKeys, browserUrlKeys } from './urlKeys';

// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
const queryToFilterConversion: OverrideValues<Partial<Filters>, (value: any) => any> = {
    sort: queryToString,
    page: queryToString,
    origin: queryToString,
    destinations: queryToDestinations,
    meals: queryToCheckboxMap,
    facilities: queryToCheckboxMap,
    activities: queryToCheckboxMap,
    priceRange: queryToNumberRange,
    keywords: queryToString,
    suppliers: queryToCheckboxMap,
    rating: queryToCheckboxMap,
    travelMonths: queryToMonths,
    departureDate: queryToFlexibleDate,
    returnDate: queryToFlexibleDate,
    duration: queryToNumberRange,
    hotelId: queryToString,
    distanceToBeach: queryToNumberRange,
    distanceToCity: queryToNumberRange,
    numberOfAdults: queryToNumber,
    agesOfChildren: queryToNumbers,
};

// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
const filterValueToQueryValueConversion: OverrideValues<Partial<Filters>, (value: any) => any> = {
    sort: stringToQuery,
    page: stringToQuery,
    origin: stringToQuery,
    destinations: destinationsToQuery,
    meals: checkboxMapToQuery,
    facilities: checkboxMapToQuery,
    activities: checkboxMapToQuery,
    priceRange: numberRangeToQuery,
    keywords: stringToQuery,
    suppliers: checkboxMapToQuery,
    distanceToBeach: numberRangeToQuery,
    distanceToCity: numberRangeToQuery,
    rating: checkboxMapToQuery,
    travelMonths: monthsToQuery,
    departureDate: flexibleDateToQuery,
    returnDate: flexibleDateToQuery,
    duration: numberRangeToQuery,
    hotelId: stringToQuery,
    numberOfAdults: numberToQuery,
    agesOfChildren: numbersToQuery,
};

const queryParamsToFilters = <T>(
    queryParams: ParsedUrlQuery,
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
    conversionObject: OverrideValues<T, (something: any) => any>,
    displayConversion?: OverrideValues<T, Record<string, string>>,
): Partial<T> => {
    const filters: Partial<T> = {};
    for (const [key, value] of Object.entries(queryParams)) {
        const conversion = conversionObject[key as keyof T];
        let escapedQueryParamValue = value;
        if (value) {
            const XssOptions = {
                whiteList: {},
                stripIgnoreTag: true,
                stripIgnoreTagBody: ['script'],
            };
            if (Array.isArray(value)) {
                escapedQueryParamValue = value.map((val) => filterXSS(val.toString(), XssOptions));
            } else {
                escapedQueryParamValue = filterXSS(value.toString(), XssOptions);
            }
        }
        if (conversion) {
            if (displayConversion?.[key as keyof T]) {
                filters[key as keyof T] = conversion(fromUrlValues(escapedQueryParamValue, displayConversion[key as keyof T]));
            } else {
                filters[key as keyof T] = conversion(escapedQueryParamValue);
            }
        }
    }
    return filters;
};

const filtersToQueryParams = <T>(
    filter: Partial<T>,
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
    conversionObject: OverrideValues<T, (something: any) => any>,
    displayConversion?: OverrideValues<T, Record<string, string>>,
    ignoredFilters?: string[],
): ParsedUrlQueryInput => {
    const queryParams: ParsedUrlQueryInput = {};
    for (const [key, value] of Object.entries(filter)) {
        if (!ignoredFilters || !ignoredFilters.includes(key)) {
            const conversion = conversionObject[key as keyof T];
            if (conversion) {
                let converted = conversion(value);
                if (converted) {
                    if (displayConversion?.[key as keyof T]) {
                        converted = toUrlValues(converted, displayConversion[key as keyof T]);
                    }
                    queryParams[key] = converted;
                }
            }
        }
    }
    return queryParams;
};

export const browserQueryParamsToFilters = (queryParams: ParsedUrlQuery): Partial<Filters> =>
    queryParamsToFilters(fromUrlKeys(queryParams, browserUrlKeys), queryToFilterConversion, browserUrlDisplayValues);

export const filtersToApiQueryParams = (filter: Partial<Filters>): ParsedUrlQueryInput =>
    toUrlKeys(filtersToQueryParams(filter, filterValueToQueryValueConversion, undefined, filter.ignoredFilters), apiUrlKeys);

export const filtersToBrowserQueryParams = (filter: Partial<Filters>): ParsedUrlQueryInput =>
    toUrlKeys(
        filtersToQueryParams(filter, filterValueToQueryValueConversion, browserUrlDisplayValues, filter.ignoredFilters),
        browserUrlKeys,
    );

export const addFiltersToUrl = (baseUrl: string, filter: Partial<Filters>): string => {
    const filterQueryParams = querystring.stringify(filtersToBrowserQueryParams(filter));
    if (filterQueryParams) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}${filterQueryParams}`;
    }
    return baseUrl;
};

export const addHotelPageFiltersToUrl = (baseUrl: string, filters: Partial<Filters>, paxEnabled?: boolean): string => {
    const filtersWithoutHotelId: Partial<Filters> = {
        ...filters,
        page: undefined,
        numberOfAdults: paxEnabled ? filters.numberOfAdults : undefined,
        agesOfChildren: paxEnabled ? filters.agesOfChildren : [],
    };
    return addFiltersToUrl(baseUrl, filtersWithoutHotelId);
};

export const addResultPageFiltersToUrl = (baseUrl: string, filters: Partial<Filters>): string => {
    const filtersWithoutHotelId: Partial<Filters> = {
        ...filters,
        hotelId: undefined,
    };
    return addFiltersToUrl(baseUrl, filtersWithoutHotelId);
};

export const browserQueryParamFor = (filterName: string, value: string): string =>
    `${toUrlKey(filterName as keyof Filters, browserUrlKeys)}=${encodeURIComponent(value)}`;
